// WhatsApp Cloud API admin panel — CS-1 (بند د1): ظهور التبويبات من الخادم.
//
// السبب الجذري المُصلَح: الظهور كان يُحسب هنا بحروف أدوار (`user.role ===
// "admin"`) ومفاتيح مختارة يدوياً، فظهرت للمندوبة تبويبات يرفضها الخادم
// («قائمة الاستبعاد» ⇒ «مخصّص للمدير/CEO»). الآن الواجهة لا تحسب شيئاً:
// `GET /api/whatsapp/panel-tabs` يُعيد كل تبويب مع الشرط الذي يفحصه مساره
// الخلفي نفسه (مفاتيح user_has + أدوار المسار القائم كبيانات) وهل هو مرئي.
import { LaylaLink } from "./LaylaLink";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import AlertThresholdsPanel from "./whatsapp/AlertThresholdsPanel";
import HealthMonitorPanel from "./whatsapp/HealthMonitorPanel";
import ReportsAdminPanel from "./whatsapp/ReportsAdminPanel";
import WABulkThresholdPanel from "./whatsapp/WABulkThresholdPanel";
import WACategoryPricesPanel from "./whatsapp/WACategoryPricesPanel";
import WASettingsOverview from "./WASettingsOverview";
import WATestNumbersPanel from "./WATestNumbersPanel";
import StatusPanel from "./whatsapp/StatusPanel";
import LogPanel from "./whatsapp/LogPanel";
import SentimentPanel from "./whatsapp/SentimentPanel";
import AdminPhonesPanel from "./whatsapp/AdminPhonesPanel";
import AutoReplyPanel from "./whatsapp/AutoReplyPanel";
import MessageLogPanel from "./whatsapp/MessageLogPanel";
import SendOverviewPanel from "./whatsapp/SendOverviewPanel";
import AuditLogPanel from "./whatsapp/AuditLogPanel";
import DailyBriefingPanel from "./whatsapp/DailyBriefingPanel";

export default function WhatsAppPanel() {
  const [group, setGroup] = useState("messages");
  const [tab, setTab] = useState("");
  const [gates, setGates] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/whatsapp/panel-tabs")
      .then((r) => { if (alive) setGates(r.data || null); })
      .catch(() => { if (alive) setGates({ groups: [], items: [], visible_ids: [] }); });
    return () => { alive = false; };
  }, []);

  // PROD-FIX-2 (بند 4) — رابط قديم مفضَّل/محفوظ بـ ?sub=chat يُشفى ذاتيًا
  // عند التحميل مباشرة (لا يعتمد فقط على حدث wa-goto-tab اللاحق).
  useEffect(() => {
    try {
      const sub = new URLSearchParams(window.location.search).get("sub");
      if (sub === "chat") {
        const u = new URL(window.location.href);
        u.searchParams.set("tab", "comms_hub");
        u.searchParams.set("sub", "unified");
        window.history.replaceState(null, "", u.toString());
        window.dispatchEvent(new CustomEvent("tw-navigate", { detail: { tab: "comms_hub" } }));
      }
    } catch (_e) { /* noop */ }
  }, []);

  const visible = useMemo(
    () => new Set((gates?.visible_ids || [])), [gates]);

  const GROUPS = useMemo(() => (
    (gates?.groups || []).map((g) => ({
      id: g.id, label: g.label,
      tabs: (gates?.items || [])
        .filter((t) => t.group === g.id && t.visible)
        .map((t) => ({ id: t.id, label: t.label })),
    })).filter((g) => g.tabs.length > 0)
  ), [gates]);

  const activeGroup = GROUPS.find((g) => g.id === group) || GROUPS[0] || null;
  useEffect(() => {
    if (!activeGroup) return;
    const belongs = activeGroup.tabs.some((t) => t.id === tab);
    if (!belongs && activeGroup.tabs[0]?.id) setTab(activeGroup.tabs[0].id);
  }, [activeGroup, tab]);

  // v119.224 — Deep-link from Executive Assistant CTA → Settings/Admin Phones
  useEffect(() => {
    const onGoto = (ev) => {
      const target = ev?.detail?.tab;
      if (!target) return;
      // PROD-FIX-2 (بند 4) — تبويب "chat" أُزيل (كان يكرّر الصندوق الموحّد).
      // أي رابط قديم يستهدفه يحوَّل إلى مركز التواصل ← الصندوق الموحّد،
      // لا صفحة فارغة.
      if (target === "chat") {
        try {
          const u = new URL(window.location.href);
          u.searchParams.set("tab", "comms_hub");
          u.searchParams.set("sub", "unified");
          window.history.replaceState(null, "", u.toString());
        } catch (_e) { /* noop */ }
        window.dispatchEvent(new CustomEvent("tw-navigate", { detail: { tab: "comms_hub" } }));
        return;
      }
      for (const g of GROUPS) {
        if (g.tabs.some((t) => t.id === target)) {
          setGroup(g.id);
          setTab(target);
          return;
        }
      }
    };
    window.addEventListener("wa-goto-tab", onGoto);
    return () => window.removeEventListener("wa-goto-tab", onGoto);
  }, [GROUPS]);

  return (
    <div data-testid="whatsapp-panel">
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
          💬 <span>WhatsApp — تكامل Meta Cloud API</span>
        </h3>
        <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.7 }}>
          إدارة كاملة لـWhatsApp Cloud API: المحادثات، الحملات، التدريب الذكي، والإعدادات.
          <strong> هوية الشركة، الفروع، العروض</strong> صارت من «<a
            href="#" onClick={(e) => { e.preventDefault();
              window.dispatchEvent(new CustomEvent("tw-navigate", { detail: { tab: "st" } }));
              setTimeout(() => window.dispatchEvent(new CustomEvent("tw-open-section", { detail: { id: "company-identity" } })), 250);
            }}
            data-testid="wa-header-goto-identity"
            style={{ color: "#4338ca", fontWeight: 800, textDecoration: "underline" }}>
            الإعدادات ← هوية الشركة
          </a>».
        </p>
      </div>

      {!gates && (
        <div className="card" data-testid="wa-tabs-loading"
          style={{ padding: 16, fontSize: 12.5 }}>⏳ جارٍ تحميل التبويبات…</div>
      )}

      {gates && GROUPS.length === 0 && (
        <div className="card" data-testid="wa-tabs-empty"
          style={{ padding: 16, fontSize: 12.5, background: "#FFF7ED",
                   border: "1px solid #FED7AA", color: "#9A3412" }}>
          لا تملك صلاحية أي قسم في هذه الشاشة. راجع مدير النظام.
        </div>
      )}

      {/* ── Level 1: group buttons (the 3 workflow sections) ── */}
      {GROUPS.length > 0 && (
        <div style={{
          display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap",
          padding: 8, background: "var(--beige-soft)", borderRadius: 10,
          border: "1px solid var(--border)",
        }} data-testid="wa-groups">
          {GROUPS.map((g) => (
            <GroupBtn key={g.id} id={g.id} cur={activeGroup?.id} set={(id) => {
              setGroup(id);
              const first = GROUPS.find((x) => x.id === id)?.tabs[0]?.id;
              if (first) setTab(first);
            }}>{g.label}</GroupBtn>
          ))}
        </div>
      )}

      {/* ── Level 2: sub-tabs of the selected group ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {(activeGroup?.tabs || []).map((t) => (
          <TabBtn key={t.id} id={t.id} cur={tab} set={setTab}>{t.label}</TabBtn>
        ))}
      </div>

      {tab === "status"    && visible.has("status") && <StatusPanel />}
      {tab === "sentiment" && visible.has("sentiment") && <SentimentPanel />}
      {tab === "msglog"    && visible.has("msglog") && <MessageLogPanel />}
      {tab === "autoreply" && visible.has("autoreply") && <AutoReplyPanel />}
      {tab === "settings_overview" && visible.has("settings_overview") && <WASettingsOverview />}
      {tab === "governance" && visible.has("governance") && (
        <>
          <WABulkThresholdPanel />
          <WACategoryPricesPanel />
        </>
      )}
      {tab === "optout" && visible.has("optout") && (
        <div className="card" data-testid="optout-moved-note" style={{ padding: 16 }}>
          <LaylaLink tab="optout" label="🚫 قائمة الاستبعاد" testId="wa-optout-layla-link" />
        </div>
      )}
      {tab === "test_numbers" && visible.has("test_numbers") && <WATestNumbersPanel />}
      {tab === "alerts" && visible.has("alerts") && <AlertThresholdsPanel />}
      {tab === "admins" && visible.has("admins") && <AdminPhonesPanel />}
      {tab === "audit"     && visible.has("audit") && <AuditLogPanel />}
      {tab === "briefing"  && visible.has("briefing") && <DailyBriefingPanel />}
      {tab === "health"    && visible.has("health") && <HealthMonitorPanel />}
      {tab === "reports_engine" && visible.has("reports_engine") && <ReportsAdminPanel />}
      {tab === "send_overview" && visible.has("send_overview") && <SendOverviewPanel />}
      {tab === "log"       && visible.has("log") && <LogPanel />}
    </div>
  );
}

function GroupBtn({ id, cur, set, children }) {
  const active = cur === id;
  return (
    <button type="button" onClick={() => set(id)}
      data-testid={`wa-group-${id}`}
      style={{
        padding: "9px 18px",
        borderRadius: 999,
        border: active ? "1.5px solid var(--brown-deep)" : "1px solid transparent",
        background: active ? "var(--brown-deep)" : "rgba(255,255,255,0.6)",
        color: active ? "#fff" : "var(--brown-deep)",
        fontSize: 13, fontWeight: 800, cursor: "pointer",
        transition: "all 0.15s ease",
      }}>{children}</button>
  );
}

function TabBtn({ id, cur, set, children }) {
  const active = cur === id;
  return (
    <button type="button" onClick={() => set(id)}
      data-testid={`wa-tab-${id}`}
      style={{
        padding: "8px 14px",
        borderRadius: 8,
        border: active ? "1.5px solid var(--brown-deep)" : "1px solid var(--border)",
        background: active ? "var(--brown-deep)" : "transparent",
        color: active ? "#fff" : "var(--brown-deep)",
        fontSize: 12.5, fontWeight: 700, cursor: "pointer",
      }}>{children}</button>
  );
}
